import Parse from 'parse'
import type { BackgroundEffect } from '../liveKitVideoBackground'
import { readStoredPlayerConfig, setPlayerConfigKey } from './playerConfigWrite'

/**
 * Player Configuration Interface
 * Comprehensive player settings and preferences
 */
export interface PlayerConfig {
  // Version and metadata
  version: string
  metadata: {
    schemaVersion: string
    lastUpdated: string
    createdBy: string
  }
  
  // UI preferences
  ui: {
    theme: 'light' | 'dark' | 'auto'
    fontSize: 'small' | 'medium' | 'large'
    showNotifications: boolean
    showTooltips: boolean
    compactMode: boolean
    musicVolume: number
    sfxVolume: number
    voiceVolume: number
    animations: boolean
    soundEffects: boolean
  }
  
  // Gameplay preferences
  gameplay: {
    defaultStatus: 'online' | 'away' | 'busy' | 'offline'
    proximityDistance: number
    movementSpeed: 'slow' | 'normal' | 'fast'
    showPlayerNames: boolean
    showPlayerStatus: boolean
    showPlayerAvatars: boolean
    autoJoinVideo: boolean
    autoJoinAudio: boolean
    proximityChat: boolean
  }
  
  // Privacy settings
  privacy: {
    showOnlineStatus: boolean
    showLastSeen: boolean
    allowDirectMessages: boolean
    allowScreenshots: boolean
    allowRecording: boolean
    showProfileTo: 'everyone' | 'company' | 'friends' | 'none'
    allowFriendRequests: boolean
    showProfileToOthers: boolean
    shareActivityStatus: boolean
  }
  
  // Workspace preferences
  workspace: {
    defaultRoom: string
    favoriteRooms: string[]
    hiddenRooms: string[]
    autoJoinDefaultRoom: boolean
    autoSaveInterval: number
    showRoomOccupancy: boolean
    enableRoomBookmarks: boolean
  }
  
  // Communication preferences
  communication: {
    defaultChannel: string
    allowVoiceChat: boolean
    allowVideoChat: boolean
    autoReply: string
    notificationSounds: boolean
    typingIndicator: boolean
    readReceipts: boolean
  }
  
  // Performance settings
  performance: {
    graphicsQuality: 'low' | 'medium' | 'high' | 'ultra'
    maxFPS: number
    enableParticles: boolean
    enableShadows: boolean
    enableAntiAliasing: boolean
    renderDistance: number
    enableReflections: boolean
  }
  
  // Accessibility settings
  accessibility: {
    highContrast: boolean
    reducedMotion: boolean
    screenReader: boolean
    keyboardNavigation: boolean
    colorBlindSupport: boolean
    textToSpeech: boolean
  }
  
  // Keybindings
  keybindings: {
    [key: string]: string
  }
  
  // Advanced settings
  advanced: {
    logLevel: 'debug' | 'info' | 'warn' | 'error'
    customCSS: string
    customJS: string
    debugMode: boolean
    enableAnalytics: boolean
    enableCrashReporting: boolean
  }

  /**
   * Audio/video pipeline preferences (SC-158).
   *
   * Optional so configs written before this section existed still satisfy the
   * type — the cloud `deepMerge` preserves unknown keys but does not invent
   * missing ones, so every reader must tolerate its absence.
   */
  media?: {
    backgroundEffect?: BackgroundEffect
  }
}

/**
 * Simplified room-specific configuration interface
 * Focused on essential data that can be loaded during loading screen
 */
export interface RoomConfig {
  // Room-specific gameplay settings
  gameplay: {
    autoJoinVideo: boolean
    autoJoinAudio: boolean
    proximityChat: boolean
    proximityDistance: number
    movementSpeed: 'slow' | 'normal' | 'fast'
  }
  
  // Room-specific privacy settings
  privacy: {
    showOnlineStatus: boolean
    allowDirectMessages: boolean
    allowScreenshots: boolean
    allowRecording: boolean
  }
  
  // Room-specific communication settings
  communication: {
    allowVoiceChat: boolean
    allowVideoChat: boolean
    autoReply: string
    defaultChannel: string
  }
  
  // Room metadata (essential tracking)
  metadata: {
    lastVisited: string
    visitCount: number
    lastPosition: {x: number, y: number}
    customSettings: boolean
  }
  
  // Room-specific performance settings
  performance: {
    graphicsQuality: 'low' | 'medium' | 'high' | 'ultra'
    maxFPS: number
    enableParticles: boolean
    enableShadows: boolean
  }
}

/**
 * Simplified default room configuration
 */
export const DEFAULT_ROOM_CONFIG: RoomConfig = {
  gameplay: {
    autoJoinVideo: false,
    autoJoinAudio: true,
    proximityChat: true,
    proximityDistance: 5,
    movementSpeed: 'normal',
  },
  privacy: {
    showOnlineStatus: true,
    allowDirectMessages: true,
    allowScreenshots: true,
    allowRecording: false,
  },
  communication: {
    allowVoiceChat: true,
    allowVideoChat: true,
    autoReply: '',
    defaultChannel: 'general',
  },
  metadata: {
    lastVisited: new Date().toISOString(),
    visitCount: 0,
    lastPosition: {x: 0, y: 0},
    customSettings: false,
  },
  performance: {
    graphicsQuality: 'medium',
    maxFPS: 60,
    enableParticles: true,
    enableShadows: true,
  },
}

/**
 * Player Configuration Service
 * Manages player-specific configurations and preferences
 */
export class PlayerConfigService {
  private static instance: PlayerConfigService
  private ProfileClass: any

  private constructor() {
    this.ProfileClass = Parse.Object.extend('Profile')
  }

  static getInstance(): PlayerConfigService {
    if (!PlayerConfigService.instance) {
      PlayerConfigService.instance = new PlayerConfigService()
    }
    return PlayerConfigService.instance
  }

  /**
   * Validate user authentication
   */
  private validateAuth(): Parse.User {
    const currentUser = Parse.User.current()
    if (!currentUser) {
      throw new Error('User not authenticated')
    }
    return currentUser
  }

  /**
   * The signed-in user's own Profile row: the one keyed by `userId`.
   *
   * Every Profile the cloud creates (sign-up, Google sign-in, invitations,
   * company and billing flows) carries only `userId`, never a `user` pointer,
   * and RoomConfigService, authService and the cloud's playerConfigManager all
   * find it this way, with `new Parse.Query`: a class from `Parse.Object.extend`
   * has no static `query()` in parse 6.1.1, the version this client has pinned
   * since it first used parse.
   */
  private findOwnProfile(currentUser: Parse.User) {
    return new Parse.Query(this.ProfileClass).equalTo('userId', currentUser.id).first()
  }

  /**
   * Store `media` preferences, leaving every other key exactly as stored.
   *
   * Never a read of the whole config that answers a failure with the defaults,
   * followed by a write of the whole field: that pair turns one network blip
   * into the defaults written over the user's config — including
   * `roomConfigs`, the per-room spawn points that RoomConfigService keeps in
   * the same field. Here a failed read throws and nothing is written, and only
   * the `media` key changes.
   *
   * No profile is an error rather than a reason to create one: Profile rows are
   * created by the cloud sign-up and invitation flows, and one made from here
   * would hold nothing but this preference.
   *
   * The write names `playerConfig.media`, so the request carries that key alone
   * and Parse Server changes nothing else in the field. Sending the whole field
   * back would put the copy read a moment ago over anything written since, such
   * as spawn points RoomConfigService saved from this tab, another tab or an
   * admin in between. With no `playerConfig` object stored there is nothing to
   * keep, and the whole field is written instead (see `setPlayerConfigKey`).
   */
  async updateMediaConfig(media: NonNullable<PlayerConfig['media']>): Promise<void> {
    const currentUser = this.validateAuth()
    const profile = await this.findOwnProfile(currentUser)
    if (!profile) {
      throw new Error('No profile to store media preferences on')
    }
    // The save goes out under whatever session exists when it is sent. One that
    // changed while the read was out would put this user's choice on a row
    // under someone else's session, so nothing is written.
    if (Parse.User.current()?.id !== currentUser.id) {
      throw new Error('Session changed before media preferences were saved')
    }

    const base: Partial<PlayerConfig> | null = readStoredPlayerConfig(profile)
    const nextMedia = { ...(base?.media ?? {}), ...media }
    setPlayerConfigKey(profile, 'media', nextMedia)
    await profile.save()
  }

  /**
   * The stored `media` preferences, or `null` when none are stored.
   *
   * Reads the same row `updateMediaConfig` writes. Throws when the read fails,
   * rather than answering with defaults, so a caller can tell "nothing stored"
   * from "could not ask" and keep what it already has.
   *
   * Only the user's own row is read. No `{ user, playerConfig }` row can hold a
   * choice: the only client code that would have written one called the static
   * `ProfileClass.query()`, so it always threw before reaching Parse (see
   * `findOwnProfile`).
   */
  async getMediaConfig(): Promise<PlayerConfig['media'] | null> {
    const currentUser = this.validateAuth()
    const own = await this.findOwnProfile(currentUser)
    return (own?.get('playerConfig') as Partial<PlayerConfig> | undefined)?.media ?? null
  }
}

// Export singleton instance
export const playerConfigService = PlayerConfigService.getInstance()
